import { useIntl } from "react-intl";
import {
	TransferToIvrRequest,
	TransferDestination,
	TransferToIvrProps,
} from "./types";
import { useStyle } from "./style";
import { useMessage } from "@/common/messages";
import { Flex } from "@/components/atoms/flex";
import {
	Text,
	RadioGroup,
	Radio,
} from "@fluentui/react-components";
import { useQuery } from "@tanstack/react-query";
import {
	getColdTransferToIvrDestinations,
	startTransferIvr,
	getGenericTransferDestinations,
	transferToQueueGeneric,
	getEmailTransferDestinations,
	transferToQueueEmail,
	getWhatsAppTransferDestinations,
	transferToQueueWhatsApp,
	getWebChatTransferDestinations,
	transferToQueueWebChat,
} from "@/repository/queryFns";
import { transferVoicemailToQueue, getVoicemailTransferDestinations } from "@/repository/queryFns/voicemails";
import {
	useCallback,
	useEffect,
	useState,
} from "react";
import { ColdTransferToQueue20Regular } from "@/common/icons";
import { TransferStartResponse, UnsupportedContactType } from "@/common/types";
import { TransferChannelType, TransferType, InitiateTransferRequest } from "@/common/types/transfer";
import {
	AppCacheState,
	AppSettingsState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import {
	CallSessionActions,
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import { StorageKeys } from "@/common/constants/storageKeys";
import { Lockr } from "@/common/Lockr";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { WarmTransfer } from "@/common/constants";
import { IvrTransferDestinationSearch } from "../transferDestinationSearch";

export const InteractionTransferIvrPopover = (props: TransferToIvrProps) => {
	const {
		closePopover,
		channelType = TransferChannelType.Voice,
		messagingId,
		onTransferCallback,
	} = props;
	const intl = useIntl();
	const classes = useStyle();
	const { show: showMessage } = useMessage();

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const [transferType, setTransferType] = useState(
		TransferType.IvrColdTransfer
	);

	const actions = useCallSessionStore((actions: CallSessionActions) => actions);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);

	const isVoicemail = channelType === TransferChannelType.Voicemail;

	const queryKey = ["transfer-destinations", channelType];

	const destinationsMap = {
		[TransferChannelType.Voice]: getColdTransferToIvrDestinations,
		[TransferChannelType.Generic]: getGenericTransferDestinations,
		[TransferChannelType.Email]: getEmailTransferDestinations,
		[TransferChannelType.WhatsApp]: getWhatsAppTransferDestinations,
		[TransferChannelType.WebChat]: getWebChatTransferDestinations,
		[TransferChannelType.Voicemail]: getVoicemailTransferDestinations,
	};

	useEffect(() => {
		if (props.channelType === TransferChannelType.Voice) {
			setTransferType(
				appSettings.callPreferredTransferIVRType === WarmTransfer
					? TransferType.IvrWarmTransfer
					: TransferType.IvrColdTransfer
			);
		}
	}, [appSettings.callPreferredTransferIVRType]);


	const { data: queriedDestinations, isLoading: queriedLoading } =
		useQuery<TransferDestination[]>({
			queryKey: queryKey,
			queryFn: destinationsMap[channelType],
			refetchOnMount: "always",
		});

	const transferDestinations = queriedDestinations;
	const transferDestinationsLoading = queriedLoading;

	const initiateTransfer = useCallback((destination: TransferDestination) => {
		const transferToIvr: TransferToIvrRequest = {
			targetId: destination.id,
			targetName: destination.name,
			type: destination.type,
			warm: transferType == TransferType.IvrWarmTransfer,
			messagingId:
				channelType === TransferChannelType.Voice
					? ""
					: messagingId ?? appStoreCache[CacheKey.CurrentInteractionId],
			transferInitiatorUserId: Lockr.get(StorageKeys.UserId),
		};

		const startTransferWithDestination = (transferToIvrRequest: TransferToIvrRequest) => {
			addBreadcrumb("ui_interaction", "Starting transfer to ivr", {
				channelType,
				...transferToIvrRequest,
			});

			switch (channelType) {
				case TransferChannelType.Voice:
					return startTransferIvr(transferToIvrRequest);
				case TransferChannelType.Generic:
					return transferToQueueGeneric({
						transferTarget: transferToIvrRequest.targetId,
						messagingId: transferToIvrRequest.messagingId,
					} as InitiateTransferRequest);
				case TransferChannelType.Email:
					return transferToQueueEmail({
						transferTarget: transferToIvrRequest.targetId,
						messagingId: transferToIvrRequest.messagingId,
						transferInitiatorUserId: transferToIvrRequest.transferInitiatorUserId,
					} as InitiateTransferRequest);
				case TransferChannelType.WhatsApp:
					return transferToQueueWhatsApp({
						transferTarget: transferToIvrRequest.targetId,
						messagingId: transferToIvrRequest.messagingId,
						transferInitiatorUserId: transferToIvrRequest.transferInitiatorUserId,
					} as InitiateTransferRequest);
				case TransferChannelType.WebChat:
					return transferToQueueWebChat({
						transferTarget: transferToIvrRequest.targetId,
						messagingId: transferToIvrRequest.messagingId,
						transferInitiatorUserId: transferToIvrRequest.transferInitiatorUserId,
					} as InitiateTransferRequest);
				case TransferChannelType.Voicemail:
					return transferVoicemailToQueue(
						transferToIvrRequest.messagingId ?? '',
						transferToIvrRequest.targetId
					).then((success): TransferStartResponse => ({
						transferStarted: success,
						transferAlreadyInProgress: false,
						contactNotFound: !success,
						contactUnavailable: false,
						invalidCall: false,
						routeCallDisabled: false,
						contactNotSupported: UnsupportedContactType.NA,
						multipleContactsWithSameSip: false,
					}));
			}

			return Promise.reject(new Error("Unknown channel type"));
		};

		startTransferWithDestination(transferToIvr)
			.then((response: TransferStartResponse) => {
				if (response.transferStarted) {
					if (onTransferCallback) {
						onTransferCallback();
					}

					if (isVoicemail) {
						showMessage("global", {
							intent: "success",
							text: intl.formatMessage({
								id: "Interaction.Voicemails.TransferToQueueSuccess",
							}),
						});
					} else if (transferType == TransferType.IvrWarmTransfer) {
						actions.update({
							...callSession,
							state: AgentInteractionStatus.WarmTransferInviteSent,
							isIVRWarmTransfer: true,
							queueName: destination.name,
						});
					} else {
						showMessage("workspace", {
							intent: "success",
							text: intl.formatMessage({
								id: "Transfer.ColdTransferPerformed",
							}),
						});
					}
				} else {
					if (response.invalidCall) {
						showMessage("workspace", {
							intent: "error",
							text: intl.formatMessage({
								id: "Transfer.ErrorMessage.InvalidCall",
							}),
						});

						return;
					}

					showMessage("workspace", {
						intent: "error",
						text: intl.formatMessage({
							id: "Transfer.ErrorMessage.TransferNotStartedFromUnknownReasons",
						}),
					});
				}
			})
			.catch((err: any) => {
				captureError(err, "Error starting transfer to ivr", undefined, {
					transferType,
					channelType,
					...transferToIvr,
				});
				showMessage("workspace", {
					intent: "error",
					text:
						transferType == TransferType.IvrWarmTransfer
							? intl.formatMessage({
									id: "Transfer.ErrorMessage.WarmTransferFailed",
								})
							: intl.formatMessage({
									id: "Transfer.ErrorMessage.ColdTransferFailed",
								}),
				});
			});
	}, [transferType, channelType, messagingId, appStoreCache, onTransferCallback, actions, callSession, showMessage, intl]);

	const queueFilter = useCallback((queue: TransferDestination) => {
		return transferType == TransferType.IvrWarmTransfer
			? queue.id !== callSession?.queueId
			: true;
	}, [transferType, callSession?.queueId]);

	const getPlaceholderKey = () => {
		if (channelType === TransferChannelType.Voice) {
			return transferType == TransferType.IvrWarmTransfer
				? "TypeQueuePlaceholder"
				: "TypeQueueOrFlowPlaceholder";
		}
		return "TypeQueuePlaceholder";
	};

	const placeholderText = intl.formatMessage({
		id: `Transfer.${getPlaceholderKey()}`,
	});

	const getTitleKey = () => {
		if (channelType === TransferChannelType.Voice) {
			return transferType == TransferType.IvrColdTransfer
				? "ColdTransferIvr"
				: "WarmTransferToQueue";
		}
		if (isVoicemail) {
			return "Voicemail.TransferToQueue";
		}
		return "ColdTransferToQueue";
	};

	return (
		<Flex column className={classes.container}>
			<Text weight="semibold" size={200} className={classes.title}>
				{intl.formatMessage({
					id: `Transfer.${getTitleKey()}`,
				})}
			</Text>
			{channelType === TransferChannelType.Voice && (
				<RadioGroup
					layout="horizontal"
					className={classes.transferType}
					value={transferType.toString()}
					onChange={(_, data) =>
						setTransferType(Number(data.value) as TransferType)
					}
				>
					<Radio
						value={TransferType.IvrColdTransfer.toString()}
						label={intl.formatMessage({
							id: "Interaction.Buttons.ColdTransfer",
						})}
						disabled={!appSettings.callColdTransferIVREnabled}
					/>
					<Radio
						value={TransferType.IvrWarmTransfer.toString()}
						label={intl.formatMessage({
							id: "Interaction.Buttons.WarmTransfer",
						})}
						disabled={!appSettings.callWarmTransferIVREnabled}
					/>
				</RadioGroup>
			)}
			<IvrTransferDestinationSearch
				destinations={transferDestinations || []}
				isLoading={transferDestinationsLoading}
				placeholder={placeholderText}
				onCancel={closePopover}
				onAction={initiateTransfer}
				actionIcon={<ColdTransferToQueue20Regular />}
				showFlows={channelType === TransferChannelType.Voice && transferType === TransferType.IvrColdTransfer}
				queueFilter={queueFilter}
			/>
		</Flex>
	);
};
